import React from "react";
import {
  DatePicker,
  Input,
  Tooltip,
  InputNumber,
  Button,
  ConfigProvider,
} from "antd";
import {
  SearchOutlined,
  QuestionCircleOutlined,
  YoutubeOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";
import { AsyncPaginate } from "react-select-async-paginate";
import {
  asyncPaginateStyles,
  loadBrandOptions,
  loadCategoryOptions,
  loadSubcategoryOptions,
  loadSubgroupOptions,
  loadNominationOptions,
  loadTypeOptions,
} from "../../../utils/getProductListCardDetails";
import ExcludableMultiSelect from "./ExcludableMultiSelect";
import ruRU from "antd/es/locale/ru_RU";
import dayjs from "dayjs";

const ProductAnalyticsFilters = ({
  // Date range
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,

  // Search
  searchValue,
  onSearchValueChange,
  onSearch,
  onClearSearch,

  // Selection filters
  selectedBrand,
  onBrandChange,
  selectedName,
  onNameChange,
  selectedCategory,
  onCategoryChange,
  selectedSubcategory,
  onSubcategoryChange,
  selectedSubgroup,
  onSubgroupChange,
  selectedType,
  onTypeChange,

  // "Select all (except)" toggles — brand + one per category level
  allBrand,
  onAllBrandChange,
  allCategory,
  onAllCategoryChange,
  allSubcategory,
  onAllSubcategoryChange,
  allSubgroup,
  onAllSubgroupChange,
  allType,
  onAllTypeChange,

  // Range filters
  priceMin,
  priceMax,
  onPriceMinChange,
  onPriceMaxChange,
  sellersMin,
  sellersMax,
  onSellersMinChange,
  onSellersMaxChange,
  ordersMin,
  ordersMax,
  onOrdersMinChange,
  onOrdersMaxChange,
  revenueMin,
  revenueMax,
  onRevenueMinChange,
  onRevenueMaxChange,

  // Actions
  onFilterSearch,
  onYouTubeClick,
}) => {
  // When a filter is in "select all" mode, its selected values are
  // EXCLUSIONS, not inclusions — so they must not narrow the other filters'
  // option lists. Gate the cross-filter narrowing arguments accordingly.
  const effBrand = allBrand ? [] : selectedBrand;
  const effCategory = allCategory ? [] : selectedCategory;
  const effSubcategory = allSubcategory ? [] : selectedSubcategory;
  const effSubgroup = allSubgroup ? [] : selectedSubgroup;
  const effType = allType ? [] : selectedType;

  // Tolerates array (multi-select), a single {value} object, or null so a
  // stray non-array value never throws during render (which blanks the page).
  const keyOf = (selected, fallback) =>
    (Array.isArray(selected)
      ? selected.map((s) => s.value).join(",")
      : selected?.value) || fallback;

  // One config entry per category level. "Select all (except)" lives inside
  // each dropdown (pinned "Выбрать все" row) and is only enabled when the
  // consumer supplies a handler — e.g. Brand Analytics doesn't opt in.
  const categoryLevels = [
    {
      label: "Категория 1:",
      placeholder: "Категория 1",
      value: selectedCategory,
      onChange: onCategoryChange,
      selectAll: allCategory,
      onSelectAllChange: onAllCategoryChange,
      instanceKey: `${keyOf(effBrand, "no-brand")}-${keyOf(
        selectedName,
        "no-name"
      )}-${keyOf(effSubcategory, "no-subcategory")}-${keyOf(
        effSubgroup,
        "no-subgroup"
      )}-${keyOf(effType, "no-type")}`,
      loadOptions: (searchValue, loadedOptions, additional) =>
        loadCategoryOptions(
          searchValue,
          loadedOptions,
          additional,
          effBrand,
          selectedName,
          effSubcategory,
          effSubgroup,
          effType
        ),
    },
    {
      label: "Категория 2:",
      placeholder: "Категория 2",
      value: selectedSubcategory,
      onChange: onSubcategoryChange,
      selectAll: allSubcategory,
      onSelectAllChange: onAllSubcategoryChange,
      instanceKey: `${keyOf(effBrand, "no-brand")}-${keyOf(
        selectedName,
        "no-name"
      )}-${keyOf(effCategory, "no-category")}-${keyOf(
        effSubgroup,
        "no-subgroup"
      )}-${keyOf(effType, "no-type")}`,
      loadOptions: (searchValue, loadedOptions, additional) =>
        loadSubcategoryOptions(
          searchValue,
          loadedOptions,
          additional,
          effBrand,
          selectedName,
          effCategory,
          effSubgroup,
          effType
        ),
    },
    {
      label: "Категория 3:",
      placeholder: "Категория 3",
      value: selectedSubgroup,
      onChange: onSubgroupChange,
      selectAll: allSubgroup,
      onSelectAllChange: onAllSubgroupChange,
      instanceKey: `${keyOf(effBrand, "no-brand")}-${keyOf(
        selectedName,
        "no-name"
      )}-${keyOf(effCategory, "no-category")}-${keyOf(
        effSubcategory,
        "no-subcategory"
      )}-${keyOf(effType, "no-type")}`,
      loadOptions: (searchValue, loadedOptions, additional) =>
        loadSubgroupOptions(
          searchValue,
          loadedOptions,
          additional,
          effBrand,
          selectedName,
          effCategory,
          effSubcategory,
          effType
        ),
    },
    {
      label: "Категория 4:",
      placeholder: "Категория 4",
      value: selectedType,
      onChange: onTypeChange,
      selectAll: allType,
      onSelectAllChange: onAllTypeChange,
      instanceKey: `${keyOf(effBrand, "no-brand")}-${keyOf(
        selectedName,
        "no-name"
      )}-${keyOf(effCategory, "no-category")}-${keyOf(
        effSubcategory,
        "no-subcategory"
      )}-${keyOf(effSubgroup, "no-subgroup")}`,
      loadOptions: (searchValue, loadedOptions, additional) =>
        loadTypeOptions(
          searchValue,
          loadedOptions,
          additional,
          effBrand,
          selectedName,
          effCategory,
          effSubcategory,
          effSubgroup
        ),
    },
  ];

  return (
    <div className="checkout wizard7 global-shadow border-0 px-sm-50 px-20 pt-sm-50 py-30 mb-30 bg-white radius-xl w-100">
      <div className="row align-items-center mb-4">
        {/* Left side - Calendar with tooltip */}
        <div className="col-lg-6 col-md-6">
          <div className="d-flex align-items-center">
            <div className="me-3">
              <label className="form-label mb-2">Период анализа:</label>
              <div className="d-flex align-items-center">
                <ConfigProvider locale={ruRU}>
                  <DatePicker
                    picker="month"
                    placeholder="От"
                    value={fromDate}
                    onChange={onFromDateChange}
                    format="YYYY-MM"
                    className="me-2"
                    style={{ width: "120px" }}
                    allowClear={false}
                    disabledDate={(current) => {
                      if (!current) return false;

                      if (current.year() < 2024) return true;

                      const now = dayjs();
                      return (
                        current.year() > now.year() ||
                        (current.year() === now.year() && current.month() >= now.month())
                      );
                    }}
                  />
                </ConfigProvider>
                <span className="mx-2">—</span>
                <ConfigProvider locale={ruRU}>
                  <DatePicker
                    picker="month"
                    placeholder="До"
                    value={toDate}
                    onChange={onToDateChange}
                    format="YYYY-MM"
                    className="me-2"
                    style={{ width: "120px" }}
                    allowClear={false}
                    disabledDate={(current) => {
                      if (!current) return false;

                      if (current.year() < 2024) return true;

                      const now = dayjs();
                      return (
                        current.year() > now.year() ||
                        (current.year() === now.year() && current.month() >= now.month())
                      );
                    }}
                  />
                </ConfigProvider>
                <Tooltip
                  title="Выберите начальный и конечный месяц для анализа данных. Система покажет статистику за выбранный период времени."
                  placement="top"
                >
                  <QuestionCircleOutlined
                    style={{
                      fontSize: "16px",
                      color: "#1890ff",
                      cursor: "pointer",
                      marginLeft: "8px",
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Search field and YouTube link */}
        <div className="col-lg-6 col-md-6">
          <div className="d-flex justify-content-end align-items-end">
            <div className="d-flex align-items-end">
              <div
                onClick={onYouTubeClick}
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  color: "#ff0000",
                  fontSize: "12px",
                  fontWeight: "500",
                  textDecoration: "none",
                  marginRight: "16px",
                  whiteSpace: "nowrap",
                }}
              >
                <YoutubeOutlined
                  style={{
                    fontSize: "18px",
                    marginRight: "6px",
                  }}
                />
                Посмотри, как пользоваться
              </div>
              <div>
                <label className="form-label d-block mb-2">Поиск товара:</label>
                <Input
                  placeholder="Поиск по ссылке или артикулу"
                  value={searchValue}
                  onChange={(e) => onSearchValueChange(e.target.value)}
                  suffix={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {searchValue && (
                        <CloseCircleFilled
                          onClick={onClearSearch}
                          style={{
                            cursor: "pointer",
                            color: "#bfbfbf",
                            fontSize: "14px",
                          }}
                        />
                      )}
                      <SearchOutlined
                        onClick={onSearch}
                        style={{
                          cursor: "pointer",
                          color: "#1890ff",
                          fontSize: "16px",
                        }}
                      />
                    </div>
                  }
                  onPressEnter={onSearch}
                  style={{ width: "250px" }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="row mt-4">
        {/* Row 1 - Brand + Product name */}
        <div className="col-12 mb-3">
          <div className="row g-3">
            <div className="col-lg-6 col-md-6 col-sm-12">
              <ExcludableMultiSelect
                label="Бренд:"
                placeholder="Бренд"
                value={selectedBrand}
                onChange={onBrandChange}
                selectAll={allBrand}
                onSelectAllChange={onAllBrandChange}
                instanceKey={`${keyOf(selectedName, "no-name")}-${keyOf(
                  effCategory,
                  "no-category"
                )}-${keyOf(effSubcategory, "no-subcategory")}-${keyOf(
                  effSubgroup,
                  "no-subgroup"
                )}-${keyOf(effType, "no-type")}`}
                loadOptions={(searchValue, loadedOptions, additional) =>
                  loadBrandOptions(
                    searchValue,
                    loadedOptions,
                    additional,
                    selectedName,
                    effCategory,
                    effSubcategory,
                    effSubgroup,
                    effType
                  )
                }
              />
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12">
              <label className="form-label mb-2 d-block">Наименование:</label>
              <AsyncPaginate
                isMulti
                key={`${keyOf(effBrand, "no-brand")}-${keyOf(
                  effCategory,
                  "no-category"
                )}-${keyOf(effSubcategory, "no-subcategory")}-${keyOf(
                  effSubgroup,
                  "no-subgroup"
                )}-${keyOf(effType, "no-type")}`}
                value={selectedName}
                loadOptions={(searchValue, loadedOptions, additional) =>
                  loadNominationOptions(
                    searchValue,
                    loadedOptions,
                    additional,
                    effBrand,
                    effCategory,
                    effSubcategory,
                    effSubgroup,
                    effType
                  )
                }
                onChange={onNameChange}
                placeholder="Наименование"
                isClearable
                debounceTimeout={300}
                additional={{ page: 1 }}
                shouldLoadMore={(scrollHeight, clientHeight, scrollTop) => {
                  return (scrollTop + clientHeight) / scrollHeight >= 0.6;
                }}
                styles={asyncPaginateStyles}
              />
            </div>
          </div>
        </div>

        {/* Row 2 - Category hierarchy (4 levels) */}
        <div className="col-12 mb-3">
          <div className="row g-3">
            {categoryLevels.map((level) => (
              <div key={level.label} className="col-lg-3 col-md-6 col-sm-12">
                <ExcludableMultiSelect {...level} />
              </div>
            ))}
          </div>
        </div>

        {/* Second row - Range filters and search button */}
        <div className="col-12">
          <div className="row g-3 align-items-end">
            <div className="col-lg col-md-3 col-sm-6">
              <label className="form-label mb-2 d-block">Цена:</label>
              <div className="d-flex">
                <InputNumber
                  placeholder="От"
                  value={priceMin}
                  onChange={onPriceMinChange}
                  style={{ width: "100%", marginRight: "4px" }}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                  parser={(value) => value.replace(/\s?/g, "")}
                />
                <InputNumber
                  placeholder="До"
                  value={priceMax}
                  onChange={onPriceMaxChange}
                  style={{ width: "100%", marginLeft: "4px" }}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                  parser={(value) => value.replace(/\s?/g, "")}
                />
              </div>
            </div>
            <div className="col-lg col-md-3 col-sm-6">
              <label className="form-label mb-2 d-block">Продавцов:</label>
              <div className="d-flex">
                <InputNumber
                  placeholder="От"
                  value={sellersMin}
                  onChange={onSellersMinChange}
                  style={{ width: "100%", marginRight: "4px" }}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                  parser={(value) => value.replace(/\s?/g, "")}
                />
                <InputNumber
                  placeholder="До"
                  value={sellersMax}
                  onChange={onSellersMaxChange}
                  style={{ width: "100%", marginLeft: "4px" }}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                  parser={(value) => value.replace(/\s?/g, "")}
                />
              </div>
            </div>
            <div className="col-lg col-md-3 col-sm-6">
              <label className="form-label mb-2 d-block">Заказов:</label>
              <div className="d-flex">
                <InputNumber
                  placeholder="От"
                  value={ordersMin}
                  onChange={onOrdersMinChange}
                  style={{ width: "100%", marginRight: "4px" }}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                  parser={(value) => value.replace(/\s?/g, "")}
                />
                <InputNumber
                  placeholder="До"
                  value={ordersMax}
                  onChange={onOrdersMaxChange}
                  style={{ width: "100%", marginLeft: "4px" }}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                  parser={(value) => value.replace(/\s?/g, "")}
                />
              </div>
            </div>
            <div className="col-lg col-md-3 col-sm-6">
              <label className="form-label mb-2 d-block">Выручка:</label>
              <div className="d-flex">
                <InputNumber
                  placeholder="От"
                  value={revenueMin}
                  onChange={onRevenueMinChange}
                  style={{ width: "100%", marginRight: "4px" }}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                  parser={(value) => value.replace(/\s?/g, "")}
                />
                <InputNumber
                  placeholder="До"
                  value={revenueMax}
                  onChange={onRevenueMaxChange}
                  style={{ width: "100%", marginLeft: "4px" }}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                  parser={(value) => value.replace(/\s?/g, "")}
                />
              </div>
            </div>
            <div className="col-lg col-md-3 col-sm-6 d-flex align-items-end">
              <Button
                type="primary"
                onClick={onFilterSearch}
                style={{ width: "100%", height: "32px" }}
              >
                Поиск
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductAnalyticsFilters;
